/**
 * InstructorEdit 컴포넌트 - 강사 생성/편집 페이지
 *
 * 주요 흐름:
 * 1. URL에서 instructorId 추출 (없으면 생성 모드)
 * 2. 편집 모드인 경우 기존 강사 정보 fetch
 * 3. 폼 입력값 관리 (useState)
 * 4. 저장 버튼 클릭 시 API 호출
 * 5. 저장 후 강사 상세 페이지로 이동
 */

import { useQueryClient } from '@tanstack/react-query';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createInstructor, updateInstructor } from '../../api/instructorApi';
import { Button } from '../../components/ui/Button';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useRemoveWithConfirm } from '../../hooks';
import { useInstructorDetail } from '../../hooks/useInstructorDetail';

/**
 * 이미지 URL을 절대 경로로 변환하는 헬퍼 함수
 */
const getImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4010';
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_URL}${normalizedUrl}`;
};

const InstructorEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const instructorId = id ? parseInt(id, 10) : 0;
  const isEditMode = instructorId > 0;

  /**
   * 편집 모드인 경우 기존 강사 정보 fetch
   */
  const { data: instructor, isLoading } = useInstructorDetail(instructorId, {
    enabled: isEditMode,
  });

  /**
   * 폼 상태 관리
   */
  const [formData, setFormData] = useState({
    name: '',
    occupation: '',
    bioTitle: '',
    bioContent: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  // 프로필 이미지 파일 업로드 상태
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const removeImage = useRemoveWithConfirm({
    mutationFn: (instructorId: number) => updateInstructor(instructorId, { imageUrl: null }),
    invalidateKeys: (instructorId) => [['instructor', instructorId], ['instructors'], ['courses']],
    title: '프로필 이미지 제거',
    message: '프로필 이미지를 제거하시겠습니까?',
  });

  // Tiptap 에디터 (소개 약력용)
  const bioContentEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Image,
    ],
    content: formData.bioContent,
    onUpdate: ({ editor }) => {
      setFormData((prev) => ({ ...prev, bioContent: editor.getHTML() }));
    },
  });

  // 편집 모드인 경우 에디터 내용 업데이트
  useEffect(() => {
    if (bioContentEditor && instructor && isEditMode) {
      bioContentEditor.commands.setContent(instructor.bioContent || '');
    }
  }, [bioContentEditor, instructor, isEditMode]);

  /**
   * 편집 모드인 경우 기존 데이터로 폼 초기화
   */
  useEffect(() => {
    if (instructor && isEditMode) {
      setFormData({
        name: instructor.name || '',
        occupation: instructor.occupation || '',
        bioTitle: instructor.bioTitle || '',
        bioContent: instructor.bioContent || '',
      });
    }
  }, [instructor, isEditMode]);

  /**
   * 입력 필드 변경 핸들러
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * 프로필 이미지 파일 선택 핸들러
   */
  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      // 미리보기 생성
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
    }
  };

  /**
   * 프로필 이미지 파일 제거 핸들러
   */
  const handleRemoveImageFile = () => {
    setImageFile(null);
    setImagePreview(null);
    // 파일 입력 초기화
    const fileInput = document.getElementById('imageFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  /**
   * 취소 핸들러
   * - 편집 모드: navigate(-1)로 이전 페이지(상세 화면)로 돌아가기
   * - 생성 모드: 목록으로 이동 (replace로 히스토리 교체)
   */
  const handleCancel = () => {
    if (isEditMode) {
      // 편집 모드: 브라우저 히스토리 기반 뒤로가기
      navigate(-1);
    } else {
      // 생성 모드: 목록으로 이동 (히스토리 교체)
      navigate('/dashboard/instructors', { replace: true });
    }
  };

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 유효성 검사
    if (!formData.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }

    if (!formData.bioTitle.trim()) {
      setError('소개 제목을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && instructor) {
        // 편집 모드: 변경된 필드만 전송
        const updateData: {
          name?: string;
          occupation?: string | null;
          bioTitle?: string | null;
          bioContent?: string | null;
        } = {};

        // 필드 비교
        const trimmedName = formData.name.trim();
        if (trimmedName !== (instructor.name || '')) {
          updateData.name = trimmedName;
        }

        const trimmedOccupation = formData.occupation?.trim() || null;
        if (trimmedOccupation !== (instructor.occupation || null)) {
          updateData.occupation = trimmedOccupation;
        }

        const trimmedBioTitle = formData.bioTitle?.trim() || null;
        if (trimmedBioTitle !== (instructor.bioTitle || null)) {
          updateData.bioTitle = trimmedBioTitle;
        }

        const trimmedBioContent = formData.bioContent?.trim() || null;
        if (trimmedBioContent !== (instructor.bioContent || null)) {
          updateData.bioContent = trimmedBioContent;
        }

        // 변경사항이 있거나 이미지 파일이 있는 경우에만 API 호출
        const hasChanges = Object.keys(updateData).length > 0 || imageFile !== null;

        if (hasChanges) {
          await updateInstructor(instructorId, updateData, imageFile || undefined);

          // 캐시 무효화
          await queryClient.invalidateQueries({
            queryKey: ['instructor', instructorId],
          });
          await queryClient.invalidateQueries({ queryKey: ['instructors'] });
          await queryClient.invalidateQueries({ queryKey: ['courses'] });
        }

        // 강사 상세 페이지로 이동
        navigate(`/dashboard/instructors/${instructorId}`);
      } else {
        // 생성 모드: 모든 필드 전송
        const createData = {
          name: formData.name.trim(),
          occupation: formData.occupation?.trim() || null,
          bioTitle: formData.bioTitle?.trim() || null,
          bioContent: formData.bioContent?.trim() || null,
        };

        const newInstructor = await createInstructor(createData, imageFile || undefined);

        // 목록/코스 캐시 갱신
        await queryClient.invalidateQueries({ queryKey: ['instructors'] });
        await queryClient.invalidateQueries({ queryKey: ['courses'] });

        // 페이지 이동 후 상세 페이지에서 자동으로 최신 데이터를 로드하므로 invalidateQueries 불필요

        // 강사 상세 페이지로 이동 (히스토리 교체)
        navigate(`/dashboard/instructors/${newInstructor.id}`, {
          replace: true,
        });
      }
    } catch (error: unknown) {
      console.error('❌ 강사 수정 실패:', error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        '강사 수정에 실패했습니다. 다시 시도해주세요.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 상태
  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !instructor) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-lg font-bold text-slate-900">강사를 찾을 수 없습니다</h3>
        <p className="mb-6 text-slate-500">요청하신 강사 정보를 불러올 수 없습니다.</p>
        <Button onClick={() => navigate('/dashboard/instructors')}>목록으로 돌아가기</Button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-slate-100 p-8">
      <div className="mx-auto max-w-4xl">
        {/* 헤더 */}
        <div className="mb-8">
          <h2 className="m-0 text-3xl font-bold text-slate-800">{isEditMode ? '강사 편집' : '새 강사 만들기'}</h2>
          <p className="mt-2 text-slate-600">{isEditMode ? '강사 정보를 수정합니다.' : '새로운 강사를 생성합니다.'}</p>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* 폼 */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* 프로필 이미지 */}
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h3 className="mb-6 text-xl font-semibold text-slate-800">프로필 이미지</h3>

              {imageFile ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <img
                      src={imagePreview || ''}
                      alt="프로필 이미지 미리보기"
                      className="h-32 w-32 rounded-full border-2 border-slate-200 object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{imageFile.name}</p>
                      <p className="mt-1 text-xs text-slate-500">{(imageFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => document.getElementById('imageFile')?.click()}
                      className="rounded-lg bg-indigo-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-indigo-700">
                      이미지 변경
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveImageFile}
                      className="rounded-lg bg-slate-200 px-6 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-300">
                      제거
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {isEditMode && instructor?.imageUrl ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4">
                        <img
                          src={getImageUrl(instructor.imageUrl) || ''}
                          alt="현재 프로필 이미지"
                          className="h-32 w-32 rounded-full border-2 border-slate-200 object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        <div>
                          <p className="text-sm font-medium text-slate-700">현재 프로필 이미지</p>
                          <p className="mt-1 text-xs text-slate-500">
                            새로운 파일을 선택하면 기존 이미지를 대체합니다.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeImage.openConfirm}
                        disabled={removeImage.isPending}
                        className="rounded-lg bg-slate-200 px-6 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:opacity-50">
                        {removeImage.isPending ? '처리 중...' : '프로필 이미지 제거'}
                      </button>
                    </div>
                  ) : null}
                  <input
                    type="file"
                    id="imageFile"
                    name="imageFile"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('imageFile')?.click()}
                    className="rounded-lg border border-slate-300 px-6 py-2.5 font-medium transition-colors hover:bg-slate-50 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                    파일 선택
                  </button>
                </div>
              )}
            </div>

            {/* 기본 정보 */}
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h3 className="mb-6 text-xl font-semibold text-slate-800">기본 정보</h3>

              <div className="space-y-4">
                {/* 이름 */}
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-slate-700">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="강사 이름을 입력하세요"
                  />
                </div>

                {/* 직업 */}
                <div>
                  <label htmlFor="occupation" className="mb-2 block text-sm font-medium text-slate-700">
                    직업
                  </label>
                  <input
                    type="text"
                    id="occupation"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="직업을 입력하세요"
                  />
                </div>

                {/* 소개 제목 */}
                <div>
                  <label htmlFor="bioTitle" className="mb-2 block text-sm font-medium text-slate-700">
                    소개 제목
                  </label>
                  <input
                    type="text"
                    id="bioTitle"
                    name="bioTitle"
                    value={formData.bioTitle}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="소개 제목을 입력하세요"
                  />
                </div>
              </div>
            </div>

            {/* 소개 약력 */}
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h3 className="mb-6 text-xl font-semibold text-slate-800">소개 약력</h3>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">소개 내용</label>
                <div className="overflow-hidden rounded-lg border border-slate-300 focus-within:border-transparent focus-within:ring-2 focus-within:ring-indigo-500">
                  {bioContentEditor && (
                    <>
                      {/* 툴바 */}
                      <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 p-2">
                        <button
                          type="button"
                          onClick={() => bioContentEditor.chain().focus().toggleBold().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            bioContentEditor.isActive('bold') ? 'bg-slate-300' : ''
                          }`}>
                          <strong>B</strong>
                        </button>
                        <button
                          type="button"
                          onClick={() => bioContentEditor.chain().focus().toggleItalic().run()}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            bioContentEditor.isActive('italic') ? 'bg-slate-300' : ''
                          }`}>
                          <em>I</em>
                        </button>
                        <div className="mx-1 h-6 w-px bg-slate-300" />
                        <button
                          type="button"
                          onClick={() => setShowHtmlSource(!showHtmlSource)}
                          className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                            showHtmlSource ? 'bg-slate-300' : ''
                          }`}>
                          HTML
                        </button>
                      </div>
                      {/* 에디터 또는 HTML 소스 */}
                      {showHtmlSource ? (
                        <textarea
                          value={bioContentEditor.getHTML() || ''}
                          onChange={(e) => {
                            bioContentEditor.commands.setContent(e.target.value);
                          }}
                          className="min-h-[200px] w-full resize-y border-none p-4 font-mono text-sm outline-none"
                          placeholder="HTML 코드를 입력하세요..."
                        />
                      ) : (
                        <EditorContent editor={bioContentEditor} />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 버튼 영역 */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-lg bg-slate-200 px-6 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-300">
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg bg-indigo-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
                {isSubmitting ? '처리 중...' : isEditMode ? '저장' : '생성'}
              </button>
            </div>
          </div>
        </form>

        {/* 전체 화면 로딩 오버레이 */}
        {isSubmitting && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
            <div className="flex flex-col items-center rounded-lg bg-white p-6 text-center shadow-xl">
              <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent"></div>
              <p className="font-medium text-slate-700">{imageFile ? '이미지 업로드 중...' : '처리 중...'}</p>
              <p className="mt-1 text-sm text-slate-500">잠시만 기다려 주세요.</p>
            </div>
          </div>
        )}

        <ConfirmModal {...removeImage.getConfirmModalProps(instructorId)} />
      </div>
    </div>
  );
};

export default InstructorEdit;
